import CurrencyDisplay from "@/app/components/currencyDisplay";
import PriceAndShipping from "@/app/ec/product/[basePath]/priceAndShipping";
import calculateRetailSavings from "@/app/ec/product/[basePath]/vendor-options/calculateRetailSavings";
import getChosenPriceBreakExpiration from "@/app/ec/product/[basePath]/vendor-options/getChosenPriceBreakExpiration";
import BigDecimal from "big.js";
import { FunctionComponent, useMemo } from "react";

import style from "./priceBreakSummary.module.scss";

BigDecimal.RM = 1; // this sets BigDecimal's rounding method to ROUND_HALF_UP

/**
 *
 * @param vendorProduct
 */

const PriceBreakSummary: FunctionComponent<PriceBreakSummaryProps> = ({
  id,
  vendorOption,
  retailPrice,
  className,
  eachClassName,
  hideEach,
  shippingClassName,
  heavyShippingClassName,
  showAddMoreFreeShipping,
  addMoreFreeShippingClassName,
  priceBreaksClassName,
  showShipping = true,
  showHeavyShipping = true,
  showSavings = true,
  shippingMessageLineBreak,
  twoDayDeliverySw,
  twoDayBadgeTooltipPosition,
}) => {
  const defaultProps = {
    id: id,
    className: `${style.PriceBreakSummary} ${className ? className : "pb-3"}`,
  };

  const promoExpiration = useMemo(() => getChosenPriceBreakExpiration(vendorOption), [vendorOption]);

  if (!vendorOption) return <div {...defaultProps}></div>;

  const savingsOverRetail = retailPrice
    ? calculateRetailSavings(retailPrice, vendorOption.chosenPriceBreakUnitPrice)
    : 0;
  const showRetailPrice =
    retailPrice && retailPrice > 0 && retailPrice > vendorOption.chosenPriceBreakUnitPrice ? true : false;

  const existsPromotionPriceBreak = (): boolean => {
    return vendorOption.priceBreaks.some((vendorOrder) => {
      return vendorOrder.pmId ? true : false;
    });
  };

  const isAllPriceBreaksOnPromotion = (): boolean => {
    return vendorOption.priceBreaks.every((vendorOrder) => {
      return vendorOrder.pmId ? true : false;
    });
  };

  const getUniquePromoDescriptions = (): string[] => {
    const uniqueProomotionDescriptions: string[] = [];

    vendorOption.priceBreaks.map((pb) => {
      if (pb.pmId && pb.promoAddlDescr) {
        if (!uniqueProomotionDescriptions.includes(pb.promoAddlDescr)) {
          uniqueProomotionDescriptions.push(pb.promoAddlDescr);
        }
      }
    });
    return uniqueProomotionDescriptions;
  };

  const showPriceBreaks = (): boolean => {
    if (existsPromotionPriceBreak()) {
      return true;
    }

    return vendorOption.priceBreaks.length > 1 || vendorOption.minimumQuantityPurchasable > 1 ? true : false;
  };

  return (
    <div {...defaultProps}>
      <div>
        <PriceAndShipping
          vendorOption={vendorOption}
          priceSize="lg"
          eachClassName={`${eachClassName} me-1`}
          hideEach={hideEach}
          showShipping={showShipping}
          showHeavyShipping={showHeavyShipping}
          shippingClassName={shippingClassName ? shippingClassName : ""}
          heavyShippingClassName={heavyShippingClassName ? heavyShippingClassName : style.heavyShippingFallback}
          showNoAdditionalShippingPopover
          showAddMoreFreeShipping={showAddMoreFreeShipping}
          addMoreFreeShippingClassName={addMoreFreeShippingClassName}
          shippingMessageLineBreak={shippingMessageLineBreak}
          twoDayBadgeTooltipPosition={twoDayBadgeTooltipPosition}
        />
      </div>

      {showSavings && (showRetailPrice || showPriceBreaks()) && (
        <div className={priceBreaksClassName ? priceBreaksClassName : style.secondaryText}>
          {showRetailPrice && (
            <span>
              <del>
                <CurrencyDisplay value={retailPrice ? retailPrice.toFixed(2) : new BigDecimal(0)} />
              </del>{" "}
              {savingsOverRetail > 1 && <span className={style.savings}>{savingsOverRetail.toFixed(0)}% Savings</span>}
            </span>
          )}

          {isAllPriceBreaksOnPromotion() && (
            <>
              <span className={style.priceBreakOnPromotion}> Special: </span>

              {vendorOption.priceBreaks.map((pb, index) => {
                const showPeriod = index === 0 ? false : true;
                return (
                  <span key={pb.minQty} className={style.priceBreakOnPromotion}>
                    {showPeriod && ", "}
                    {pb.minQty} @ <CurrencyDisplay value={pb.unitPrice.toFixed(2)} />
                  </span>
                );
              })}

              {promoExpiration && (
                <>
                  <span className={style.priceBreakOnPromotion}>{" | "}</span>
                  <span className={style.expiration}>Product Expires on {promoExpiration}</span>
                </>
              )}
            </>
          )}

          {!isAllPriceBreaksOnPromotion() &&
            showPriceBreaks() &&
            vendorOption.priceBreaks.map((pb, index) => {
              const showPeriod = index !== 0 || showRetailPrice;

              return (
                <span key={pb.minQty} className="">
                  {showPeriod && ", "}
                  <span className={pb.pmId ? style.priceBreakOnPromotion : ""}>
                    {pb.pmId ? "Special: " : " "}
                    {pb.minQty} @ <CurrencyDisplay value={pb.unitPrice.toFixed(2)} />
                  </span>
                </span>
              );
            })}

          {getUniquePromoDescriptions()
            .filter((desc) => {
              const expiration = isAllPriceBreaksOnPromotion() ? promoExpiration : null;
              return !(expiration && desc.includes(expiration));
            })
            .map((desc, index) => {
              const showPeriod = index === 0 ? false : true;

              return (
                <span key={index} className={style.priceBreakOnPromotion}>
                  {showPeriod && ", "} {desc}
                </span>
              );
            })}
        </div>
      )}
    </div>
  );
};

type PriceBreakSummaryProps = {
  vendorOption?: VendorOptionState;
  retailPrice?: number;
  id?: string;
  className?: string;
  eachClassName?: string;
  /** Hide the "/ea" suffix after the price */
  hideEach?: boolean;
  shippingClassName?: string;
  heavyShippingClassName?: string;
  showAddMoreFreeShipping?: boolean;
  addMoreFreeShippingClassName?: string;
  priceBreaksClassName?: string;
  showShipping?: boolean;
  showHeavyShipping?: boolean;
  showSavings?: boolean;
  shippingMessageLineBreak?: boolean;
  twoDayDeliverySw?: boolean;
  twoDayBadgeTooltipPosition?: "top" | "bottom";
};

export default PriceBreakSummary;
